import { useParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { ProductCard } from '@/components/ProductCard';
import { Category, PRODUCT_CATEGORIES } from '@/lib/index';
import { ALL_PRODUCTS } from '@/data/products';
import { ChevronDown } from 'lucide-react';
import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';

type SortOption = 'featured' | 'price-low' | 'price-high' | 'rating';

export default function Products() {
  const { category } = useParams<{ category: string }>();
  const [sortBy, setSortBy] = useState<SortOption>('featured');

  const categoryProducts = useMemo(() => {
    if (!category || !(PRODUCT_CATEGORIES as readonly string[]).includes(category)) {
      return [];
    }
    return ALL_PRODUCTS.filter((p) => p.category === (category as Category));
  }, [category]);

  const sortedProducts = useMemo(() => {
    const products = [...categoryProducts];
    switch (sortBy) {
      case 'price-low':
        return products.sort((a, b) => a.price - b.price);
      case 'price-high':
        return products.sort((a, b) => b.price - a.price);
      case 'rating':
        return products.sort((a, b) => b.rating - a.rating);
      default:
        return products;
    }
  }, [categoryProducts, sortBy]);

  const categoryTitle = useMemo(() => {
    if (!category) return 'Products';
    return category.charAt(0).toUpperCase() + category.slice(1);
  }, [category]);

  if (!category || !(PRODUCT_CATEGORIES as readonly string[]).includes(category)) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-24 text-center">
          <h1 className="text-4xl font-bold mb-4">Category Not Found</h1>
          <p className="text-muted-foreground">The requested category does not exist.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="w-full">
        <div className="container mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <h1 className="text-6xl md:text-7xl font-black tracking-tight mb-4">
              {categoryTitle}
            </h1>
            <p className="text-lg text-muted-foreground">
              {sortedProducts.length} products available
            </p>
          </motion.div>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Sort by:</span>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="appearance-none bg-background border border-border rounded-none px-4 py-2 pr-10 text-sm font-medium cursor-pointer hover:border-foreground transition-colors"
                >
                  <option value="featured">Featured</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Highest Rated</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" />
              </div>
            </div>
          </div>

          {sortedProducts.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-xl text-muted-foreground">No products found in this category.</p>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {sortedProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
}
